import React from 'react'
import { FormattedMessage } from 'react-intl'
import { AsyncStatus, type DropdownOption } from 'legacy/common/types'
import withApi, { type WithApiInjectedProps } from 'legacy/common/withApi'
import {
  withNotificationsContext,
  type NotificationsContextValue,
} from 'legacy/contexts/notifications/NotificationsContext'
import { parseCostUnit, PRICED_BY } from 'legacy/common/utils/catalog/units'
import {
  type NewProductFormValues,
  type NewProductRequestBody,
} from 'legacy/routes/catalog/types/product.types'
import { type Brands } from 'legacy/routes/catalog/types/brands.types'

interface ChildFunctionValues extends State {
  onRequestNewProduct: (products: NewProductFormValues) => void
}

interface OwnProps {
  partner_id: string
  warehouse_id: string
  children: (values: ChildFunctionValues) => React.ReactNode
  onSuccess: () => void
}

interface State {
  product: NewProductFormValues
  requestNewProductStatus: AsyncStatus
}

type Props = OwnProps & WithApiInjectedProps & NotificationsContextValue

export const placeholderId = -1

class NewProductContainer extends React.Component<Props, State> {
  readonly state: State = {
    product: this.createBlankProduct(),
    requestNewProductStatus: AsyncStatus.Initial,
  }

  createBlankProduct(): NewProductFormValues {
    return {
      editable: true,
      name: '',
      brandName: '',
      size: '',
      productCodes: '',
      primaryImageUrl: '',
      attributes: [],
      pricedBy: PRICED_BY.EACH,
      costPricePerUnit: '',
      taxable: false,
      departmentId: placeholderId,
      aisleId: placeholderId,
      details: '',
      retailerReferenceCode: '',
    }
  }

  transformFormValuesToRequestBody(formValues: NewProductFormValues): NewProductRequestBody {
    const {
      attributes,
      aisleId,
      brandName,
      departmentId,
      details,
      productCodes,
      name,
      pricedBy,
      primaryImageUrl: remoteImageUrl,
      costPricePerUnit,
      size,
      taxable,
      retailerReferenceCode,
    } = formValues

    return {
      attributes,
      // support deprecating aisle
      aisle_id: aisleId === placeholderId ? undefined : aisleId,
      brand: brandName,
      cost_price_per_unit: costPricePerUnit,
      cost_unit: parseCostUnit(pricedBy),
      details,
      // support deprecating deparment
      department_id: departmentId === placeholderId ? undefined : departmentId,
      lookup_code: productCodes,
      retailer_reference_code: retailerReferenceCode,
      name,
      priced_by: pricedBy,
      remote_image_url: remoteImageUrl,
      size,
      taxable,
    }
  }

  transformBrandsResponseBodyToOptions = (options: Brands): DropdownOption<number>[] =>
    options.map(({ id, name }) => ({ value: id, label: name }))

  handleRequestNewProduct = async (values: NewProductFormValues) => {
    const requestBody: NewProductRequestBody = this.transformFormValuesToRequestBody(values)
    this.setState({
      requestNewProductStatus: AsyncStatus.Busy,
    })

    try {
      const { partner_id, warehouse_id, api } = this.props

      await api.post<{}, NewProductRequestBody>(
        `/v1/partners/${partner_id}/warehouses/${warehouse_id}/products/create_request`,
        {
          data: requestBody,
        }
      )

      this.setState({
        requestNewProductStatus: AsyncStatus.Done,
      })

      if (typeof this.props.onSuccess === 'function') {
        this.props.onSuccess()
      }

      this.props.notify(<FormattedMessage id="catalog.product.requestNew.notification" />)
    } catch (e) {
      this.setState({
        requestNewProductStatus: AsyncStatus.Fail,
      })
    }
  }

  render() {
    const { product, requestNewProductStatus } = this.state

    return this.props.children({
      product,
      requestNewProductStatus,
      onRequestNewProduct: this.handleRequestNewProduct,
    })
  }
}

export default withNotificationsContext(withApi(NewProductContainer))
